import * as moment from 'moment';
import { useSelector } from 'react-redux';
import type { ReactElement, VoidFunctionComponent } from 'react';

import { isSortSelected, ProjectSortPrefixes } from '../../../../../../shared/projectsV2';
import * as ProjectV2Store from '../../../../../redux/projectsV2';
import { Icon, icons } from '../../../../generic';
import FromNow from '../../../../generic/FromNow';
import Tooltip from '../../../../generic/Tooltip';
import type { Project, ProjectsSort } from '../../../../../../shared/projectsV2';

/**
 * Helper for determining the project field to use for sorting.
 */
function determineSortField(sort: ProjectsSort): 'scanned' | 'lastAnalyzed' {
  if (isSortSelected(sort, ProjectSortPrefixes.LatestScan)) {
    return 'scanned';
  }
  if (!isSortSelected(sort, ProjectSortPrefixes.LastAnalyzed)) {
    return 'scanned';
  }
  return 'lastAnalyzed';
}

/**
 * Helper for determining the user-facing name of the sort field.
 */
function determineSortName(sort: ProjectsSort): 'scanned' | 'analyzed' {
  if (isSortSelected(sort, ProjectSortPrefixes.LatestScan)) {
    return 'scanned';
  }
  if (!isSortSelected(sort, ProjectSortPrefixes.LastAnalyzed)) {
    return 'scanned';
  }
  return 'analyzed';
}

/**
 * Helper for determining the revision time message to display for a project.
 */
function determineRevisionTimeMessage(project: Project, sort: ProjectsSort): ReactElement {
  const sortField = determineSortField(sort);
  const sortName = determineSortName(sort);

  if (project.latestBuildStatus === 'failed') {
    return (
      <span>
        Failed to analyze
      </span>
    );
  }

  const scannedOrAnalyzed = project[sortField];
  if (!scannedOrAnalyzed) {
    return (
      <>
        <Icon icon={icons.circleO} hasSpin />
        <span className="margin-left-xs">
          {`Waiting to be ${sortName}...`}
        </span>
      </>
    );
  }

  return (
    <FromNow date={project[sortField]} />
  );
}

/**
 * Helper for determining the revision time tooltip to display for a project.
 */
function determineRevisionTimeTooltip(project: Project, sort: ProjectsSort): string {
  const sortField = determineSortField(sort);
  const sortName = determineSortName(sort);

  if (project.latestBuildStatus === 'failed') {
    return 'Failed to analyze. View the project for more details.';
  }

  const scannedOrAnalyzed = project[sortField];
  if (!scannedOrAnalyzed) {
    return `Project has not been ${sortName} yet.`;
  }

  return moment(project[sortField]).format('MMM Do, YYYY [at] HH:mm:ss');
}

type Props = {
  project: Project;
};

const RevisionTimes: VoidFunctionComponent<Props> = ({ project }: Props) => {
  const sort: ProjectsSort = useSelector(ProjectV2Store.selectors.getSort);
  const revisionTimeMessage = determineRevisionTimeMessage(project, sort);
  const revisionTimeTooltip = determineRevisionTimeTooltip(project, sort);

  return (
    <div>
      <Tooltip id="project-item-tooltip" tooltip={revisionTimeTooltip}>
        <span className="project-item__scanned">
          {revisionTimeMessage}
        </span>
      </Tooltip>
    </div>
  );
};

export default RevisionTimes;
